import { Link } from '@tanstack/react-router'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { env } from '~/env'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { copyToClipboard } from '~/shared/lib/share'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Disclosure } from '~/shared/ui/disclosure'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { badgeHtml, badgeImageUrl, placeReadiness, type ReadinessItem } from '../lib/readiness'

const ITEM_LABEL: Record<ReadinessItem, () => string> = {
  description: m.searchReadyItemDescription,
  hours: m.searchReadyItemHours,
  photos: m.searchReadyItemPhotos,
  phone: m.searchReadyItemPhone,
  website: m.searchReadyItemWebsite,
  email: m.searchReadyItemEmail,
  instagram: m.searchReadyItemInstagram,
}

export function SearchReadinessCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: listing } = useListing(business?.id ?? '', listingId)

  if (!listing) return null

  const { score, missing } = placeReadiness(listing)
  const locale = getLocale() === 'en' ? 'en' : 'de'
  const isDraft = listing.isDraft || listing.status === 'Draft'

  const handleCopy = async () => {
    const ok = await copyToClipboard(badgeHtml(listing, locale, env.VITE_PUBLIC_APP_URL))
    if (ok) toast.success(m.searchReadyBadgeCopied())
    else toast.error(m.failedToCopyLink())
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold font-serif">{m.searchReadyTitle()}</h3>
        <InfoTooltip label={m.searchReadyTitle()}>{m.searchReadySubtitle()}</InfoTooltip>
        <span className="ml-auto font-semibold font-serif text-brand-subtle-foreground text-xl tabular-nums">
          {score}%
        </span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-brand transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${score}%` }}
        />
      </div>

      {missing.length === 0 ? (
        <p className="flex items-center gap-2 text-sm">
          <Check className="size-4 text-brand" />
          {m.searchReadyDone()}
        </p>
      ) : (
        <p className="text-muted-foreground text-sm">
          {m.searchReadyMissing({ items: missing.map((item) => ITEM_LABEL[item]()).join(', ') })}{' '}
          <Link
            to="/dashboard/listings/$listingId/edit"
            params={{ listingId }}
            className="font-medium text-brand-subtle-foreground hover:underline"
          >
            {m.searchReadyComplete()}
          </Link>
        </p>
      )}

      {isDraft ? null : (
        <div className="border-t pt-1">
          <Disclosure title={m.searchReadyBadgeTitle()} contentClassName="flex flex-col gap-3">
            <p className="text-muted-foreground text-xs">{m.searchReadyBadgeText()}</p>
            <img
              src={badgeImageUrl(locale, env.VITE_PUBLIC_APP_URL)}
              alt=""
              width={locale === 'en' ? 182 : 204}
              height={44}
              className="h-11 w-auto self-start"
            />
            <Button variant="outline" size="sm" className="w-fit" onClick={handleCopy}>
              <Copy />
              {m.searchReadyBadgeCopy()}
            </Button>
          </Disclosure>
        </div>
      )}
    </Card>
  )
}
